// cspell:ignore unprovisioned
/**
 * SDO service calls for the watermark library (VE-24693). Resolves the latest
 * published schema id from the `watermark` data registry, caches it in module
 * scope for the life of the document, then runs list/upsert/delete against it.
 * Mirrors settingsProfile/services.
 */
import callGraphQLApi, {
  GQLApiDispatch,
  GQLApiGetState,
} from '@helpers/callGraphQLApi';
import { modules } from '@veritone/glc-redux';
import * as Actions from '../actions';
import {
  CreateWatermarkResponse,
  DeleteWatermarkResponse,
  FetchWatermarksResponse,
  WatermarkDefinition,
  WatermarkDefinitionData,
} from '../models';
import {
  deleteWatermarkQuery,
  fetchWatermarksQuery,
  upsertWatermarkQuery,
  fetchWatermarkSchemaQuery,
} from './queries';

const {
  config: { getConfig },
} = modules;

const NOOP = 'noop';

interface SchemaLookupResponse {
  dataRegistry?: { publishedSchema?: { id?: string } };
}

/**
 * The org-shared watermark registry, created with this id on every cluster.
 *
 * Hard-coded on purpose, matching `redactionStatusesRegistryId` in
 * `redactionStatuses/services`: the registry is provisioned once per
 * environment with a FORCED id (`createDataRegistry(input: { id })`), so the
 * same literal is correct everywhere and an unconfigured environment reaches
 * the real library instead of silently dropping every user onto their own
 * browser. Runtime config still wins when it is set, which is what makes a
 * per-environment override possible without a release.
 *
 * See `docs/schemas/WatermarkLibrarySchema.md` for the provisioning runbook. If the registry
 * has not been created on a cluster yet, `publishedSchema` comes back null and
 * every watermark operation FAILS — `WatermarkUnavailableNotice` says so and Add
 * New is disabled. It used to fall through to browser-local storage instead;
 * that fallback is off (`WATERMARK_LOCAL_FALLBACK_ENABLED`), because a library
 * that silently becomes per-device is a worse answer than one that says it is
 * not set up.
 */
export const DEFAULT_WATERMARK_REGISTRY_ID =
  '322b31ca-7e64-4a3b-b20d-a028202fdae2';

/**
 * Three outcomes, deliberately distinguished.
 *
 * The shared `getLatestSchemaId` collapses all of them into `undefined` — a 502,
 * a token refresh, an authorization denial and a genuinely unprovisioned
 * registry are indistinguishable to its caller. Acting on that is how a single
 * transient error diverted an entire session onto browser-local storage,
 * announced "shared storage is not set up for your organization" on a healthy
 * cluster, and orphaned every watermark written afterwards.
 *
 * `unprovisioned` is a fact about the CLUSTER: nothing will work until someone
 * publishes the schema, so the UI says that and stops offering to create.
 * `unreachable` is a fact about ONE REQUEST, so the operation fails and the user
 * retries. Keeping them apart is what stops a single 502 from labelling a
 * healthy cluster as unprovisioned for the rest of the session.
 */
export type WatermarkRegistryResolution =
  | { status: 'ready'; schemaId: string }
  | { status: 'unprovisioned' }
  | { status: 'unreachable' };

/**
 * Successes only, keyed by registry id. A failure must stay retryable — that was
 * the whole bug.
 *
 * Module scope, NOT sessionStorage (VE-28974): sessionStorage survives a page
 * reload, so once the watermark registry published a new schema version the tab
 * kept writing against the superseded id until it was *closed*, and reloading —
 * the one thing anyone tries — did nothing. Nothing here invalidates on a schema
 * validation error either, so a reload is currently the only recovery; holding
 * the cache in module scope is what makes that recovery actually work.
 */
const schemaIdCache = new Map<string, string>();

/** Drops the cached id so the next resolve goes back to the registry. */
export const clearWatermarkSchemaCache = () => {
  schemaIdCache.clear();
};

/**
 * Resolve the registry's published schema.
 *
 * Written here rather than delegating for a second reason too: `getLatestSchemaId`
 * dispatches `SCHEMA_ID_NOT_FOUND`, which a global handler turns into a red
 * "verify the system configuration file" snackbar — operator language shown to
 * every end user on a cluster where `WatermarkUnavailableNotice` is already saying the
 * same thing in words they can act on.
 */
export const resolveWatermarkRegistry =
  () =>
  async (
    dispatch: GQLApiDispatch,
    getState: GQLApiGetState
  ): Promise<WatermarkRegistryResolution> => {
    const { watermarkRegistryId } = getConfig<Window['config']>(getState());
    const dataRegistryId = watermarkRegistryId || DEFAULT_WATERMARK_REGISTRY_ID;

    const cached = schemaIdCache.get(dataRegistryId);
    if (cached) {
      return { status: 'ready', schemaId: cached };
    }

    let resp: SchemaLookupResponse | undefined;
    try {
      resp = await callGraphQLApi<SchemaLookupResponse>({
        actionTypes: [NOOP, NOOP, NOOP],
        query: fetchWatermarkSchemaQuery,
        variables: { dataRegistryId },
        dispatch,
        getState,
      });
    } catch (err) {
      console.warn('[redact] watermark registry lookup failed', err);
      return { status: 'unreachable' };
    }

    const schemaId = resp?.dataRegistry?.publishedSchema?.id;
    if (!schemaId) {
      return { status: 'unprovisioned' };
    }
    schemaIdCache.set(dataRegistryId, schemaId);
    return { status: 'ready', schemaId };
  };

/** The schema id, or undefined — for the CRUD thunks, which cannot act on why. */
const lookupLatestWatermarkSchemaId = async (
  dispatch: GQLApiDispatch,
  getState: GQLApiGetState
): Promise<string | undefined> => {
  const resolved = await resolveWatermarkRegistry()(dispatch, getState);
  return resolved.status === 'ready' ? resolved.schemaId : undefined;
};

/**
 * What a picker may be offered, as the SERVER understands it.
 *
 * **String values, not booleans, and that is not a style choice.** A boolean
 * literal makes the SDO endpoint return `internal_error` — measured, both bare
 * (`isDeleted: false`) and wrapped (`isDeleted: { eq: false }`). Strings are
 * coerced against the stored boolean and filter correctly.
 *
 * The matching stays exact, so a row that OMITS one of these keys matches
 * neither `"true"` nor `"false"` and is dropped by this filter. Measured on the
 * shared registry: of six rows, three carried neither key and vanished. That is
 * why `stampWriteDefaults` writes both on every save — this filter is only
 * correct for rows that have been through it, and it must not be pointed at a
 * cluster whose library predates that.
 */
// Deliberately WITHOUT `appliesToDocuments`. An SDO filter matches on the
// stored value, so requiring it would drop every row written before the key
// existed; the "absent means documents" rule is client-side for that reason and
// lives in `watermarkAppliesTo`, which `selectPickableWatermarks` applies to
// whatever comes back from here.
const PICKABLE_FILTER = { isDeleted: 'false', enabled: 'true' };

export const fetchWatermarks =
  (limit: number, offset: number, pickableOnly = false) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    const schemaId = await lookupLatestWatermarkSchemaId(dispatch, getState);
    if (!schemaId) {
      // A guard, not a decision. The saga resolves the registry first and only
      // reaches here when it is `ready`; deciding the fallback in this file is
      // what made it depend on a `put` that does not wait.
      return;
    }
    return await callGraphQLApi<FetchWatermarksResponse>({
      actionTypes: [
        NOOP,
        Actions.FETCH_WATERMARKS_SUCCESS,
        Actions.FETCH_WATERMARKS_FAILURE,
      ],
      query: fetchWatermarksQuery,
      variables: {
        schemaId,
        limit,
        offset,
        // `null`, not `undefined`: the variable is declared on the query, and
        // omitting it entirely is a different request from asking for no filter.
        filter: pickableOnly ? PICKABLE_FILTER : null,
      },
      dispatch,
      getState,
    });
  };

export const createWatermark =
  (data: WatermarkDefinitionData) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    const schemaId = await lookupLatestWatermarkSchemaId(dispatch, getState);
    if (!schemaId) {
      // A guard, not a decision. The saga resolves the registry first and only
      // reaches here when it is `ready`; deciding the fallback in this file is
      // what made it depend on a `put` that does not wait.
      return;
    }
    return await callGraphQLApi<CreateWatermarkResponse>({
      actionTypes: [
        NOOP,
        Actions.CREATE_WATERMARK_SUCCESS,
        Actions.CREATE_WATERMARK_FAILURE,
      ],
      query: upsertWatermarkQuery,
      variables: { data, schemaId },
      dispatch,
      getState,
    });
  };

/**
 * Upsert a definition.
 *
 * Takes `id` and `data` only — those are the two things the mutation sends, and
 * typing it as a whole `WatermarkDefinition` forced every housekeeping caller to
 * cast a partial through `as`, which turns a compiler check into a comment.
 */
export const updateWatermark =
  (watermark: Pick<WatermarkDefinition, 'id' | 'data'>) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    const schemaId = await lookupLatestWatermarkSchemaId(dispatch, getState);
    if (!schemaId) {
      // A guard, not a decision. The saga resolves the registry first and only
      // reaches here when it is `ready`; deciding the fallback in this file is
      // what made it depend on a `put` that does not wait.
      return;
    }
    return await callGraphQLApi<CreateWatermarkResponse>({
      actionTypes: [
        NOOP,
        Actions.UPDATE_WATERMARK_SUCCESS,
        Actions.UPDATE_WATERMARK_FAILURE,
      ],
      query: upsertWatermarkQuery,
      variables: { id: watermark.id, data: watermark.data, schemaId },
      dispatch,
      getState,
    });
  };

export const deleteWatermark =
  (id: string) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    const schemaId = await lookupLatestWatermarkSchemaId(dispatch, getState);
    if (!schemaId) {
      // A guard, not a decision. The saga resolves the registry first and only
      // reaches here when it is `ready`; deciding the fallback in this file is
      // what made it depend on a `put` that does not wait.
      return;
    }
    return await callGraphQLApi<DeleteWatermarkResponse>({
      actionTypes: [
        NOOP,
        Actions.DELETE_WATERMARK_SUCCESS,
        Actions.DELETE_WATERMARK_FAILURE,
      ],
      query: deleteWatermarkQuery,
      variables: { id, schemaId },
      dispatch,
      getState,
    });
  };
